import { createSlice } from "@reduxjs/toolkit";
import { message } from "antd";
import {
  fetchAllQuickTrips,
  fetchQuickTripById,
  createQuickTrip,
  updateQuickTrip,
  deleteQuickTrip,
  restoreQuickTrip,
  uploadQuickTripImages,
  fetchAllPackages,
  fetchPackageById,
  createPackage,
  updatePackage,
  deletePackage,
  restorePackage,
  uploadPackageImages,
  fetchCustomizationOptions,
  createCustomizationOption,
  updateCustomizationOption,
  deleteCustomizationOption,
  fetchPackageCustomizations,
  addPackageCustomization,
  removePackageCustomization,
  fetchAllSavedData,
  fetchAllPackageBookings,
  fetchPackageBookingById,
  fetchAllCustomPackageRequests,
  fetchCustomPackageRequestById,
  updateCustomPackageRequestStatus,
  deleteCustomPackageRequest,
} from "./packagesThunk";

const initialState = {
  quickTrips: { data: [], total: 0, page: 0, pageSize: 10 },
  selectedQuickTrip: null,
  quickTripsLoading: false,
  quickTripActionLoading: false,

  packages: { data: [], total: 0, page: 0, pageSize: 10 },
  selectedPackage: null,
  packagesLoading: false,
  packageActionLoading: false,

  customizationOptions: [],
  customizationsLoading: false,
  // per-package links, keyed by packageId
  packageCustomizations: {},
  customizationActionLoading: false,

  savedData: { data: [], total: 0, page: 0, pageSize: 10 },
  savedDataLoading: false,

  bookings: { data: [], total: 0, page: 0, pageSize: 10 },
  selectedBooking: null,
  bookingsLoading: false,

  customPackageRequests: { data: [], total: 0, page: 0, pageSize: 10 },
  selectedCustomPackageRequest: null,
  customPackageRequestsLoading: false,
  customPackageRequestActionLoading: false,
};

const packagesSlice = createSlice({
  name: "packages",
  initialState,
  reducers: {
    clearSelectedQuickTrip: (state) => {
      state.selectedQuickTrip = null;
    },
    clearSelectedPackage: (state) => {
      state.selectedPackage = null;
    },
    clearSelectedBooking: (state) => {
      state.selectedBooking = null;
    },
    clearSelectedCustomPackageRequest: (state) => {
      state.selectedCustomPackageRequest = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // ── quick trips: list ──
      .addCase(fetchAllQuickTrips.pending, (state) => {
        state.quickTripsLoading = true;
      })
      .addCase(fetchAllQuickTrips.fulfilled, (state, action) => {
        state.quickTripsLoading = false;
        state.quickTrips = action.payload;
      })
      .addCase(fetchAllQuickTrips.rejected, (state, action) => {
        state.quickTripsLoading = false;
        message.error(action.payload || "Failed to load quick trips");
      })

      // ── quick trips: single ──
      .addCase(fetchQuickTripById.pending, (state) => {
        state.quickTripsLoading = true;
      })
      .addCase(fetchQuickTripById.fulfilled, (state, action) => {
        state.quickTripsLoading = false;
        state.selectedQuickTrip = action.payload;
      })
      .addCase(fetchQuickTripById.rejected, (state, action) => {
        state.quickTripsLoading = false;
        message.error(action.payload || "Failed to load quick trip");
      })

      // ── quick trips: create/update/delete/restore ──
      .addCase(createQuickTrip.pending, (state) => {
        state.quickTripActionLoading = true;
      })
      .addCase(createQuickTrip.fulfilled, (state) => {
        state.quickTripActionLoading = false;
        message.success("Quick trip created");
      })
      .addCase(createQuickTrip.rejected, (state, action) => {
        state.quickTripActionLoading = false;
        message.error(action.payload || "Failed to create quick trip");
      })
      .addCase(updateQuickTrip.pending, (state) => {
        state.quickTripActionLoading = true;
      })
      .addCase(updateQuickTrip.fulfilled, (state) => {
        state.quickTripActionLoading = false;
        message.success("Quick trip updated");
      })
      .addCase(updateQuickTrip.rejected, (state, action) => {
        state.quickTripActionLoading = false;
        message.error(action.payload || "Failed to update quick trip");
      })
      .addCase(deleteQuickTrip.pending, (state) => {
        state.quickTripActionLoading = true;
      })
      .addCase(deleteQuickTrip.fulfilled, (state) => {
        state.quickTripActionLoading = false;
        message.success("Quick trip deleted");
      })
      .addCase(deleteQuickTrip.rejected, (state, action) => {
        state.quickTripActionLoading = false;
        message.error(action.payload || "Failed to delete quick trip");
      })
      .addCase(restoreQuickTrip.pending, (state) => {
        state.quickTripActionLoading = true;
      })
      .addCase(restoreQuickTrip.fulfilled, (state) => {
        state.quickTripActionLoading = false;
        message.success("Quick trip restored");
      })
      .addCase(restoreQuickTrip.rejected, (state, action) => {
        state.quickTripActionLoading = false;
        message.error(action.payload || "Failed to restore quick trip");
      })
      .addCase(uploadQuickTripImages.pending, (state) => {
        state.quickTripActionLoading = true;
      })
      .addCase(uploadQuickTripImages.fulfilled, (state) => {
        state.quickTripActionLoading = false;
      })
      .addCase(uploadQuickTripImages.rejected, (state, action) => {
        state.quickTripActionLoading = false;
        message.error(action.payload || "Quick trip saved, but the images failed to upload");
      })

      // ── packages: list ──
      .addCase(fetchAllPackages.pending, (state) => {
        state.packagesLoading = true;
      })
      .addCase(fetchAllPackages.fulfilled, (state, action) => {
        state.packagesLoading = false;
        state.packages = action.payload;
      })
      .addCase(fetchAllPackages.rejected, (state, action) => {
        state.packagesLoading = false;
        message.error(action.payload || "Failed to load packages");
      })

      // ── packages: single ──
      .addCase(fetchPackageById.pending, (state) => {
        state.packagesLoading = true;
      })
      .addCase(fetchPackageById.fulfilled, (state, action) => {
        state.packagesLoading = false;
        state.selectedPackage = action.payload;
      })
      .addCase(fetchPackageById.rejected, (state, action) => {
        state.packagesLoading = false;
        message.error(action.payload || "Failed to load package");
      })

      // ── packages: create/update/delete/restore ──
      .addCase(createPackage.pending, (state) => {
        state.packageActionLoading = true;
      })
      .addCase(createPackage.fulfilled, (state) => {
        state.packageActionLoading = false;
        message.success("Package created");
      })
      .addCase(createPackage.rejected, (state, action) => {
        state.packageActionLoading = false;
        message.error(action.payload || "Failed to create package");
      })
      .addCase(updatePackage.pending, (state) => {
        state.packageActionLoading = true;
      })
      .addCase(updatePackage.fulfilled, (state, action) => {
        state.packageActionLoading = false;
        state.selectedPackage = action.payload;
        message.success("Package updated");
      })
      .addCase(updatePackage.rejected, (state, action) => {
        state.packageActionLoading = false;
        message.error(action.payload || "Failed to update package");
      })
      .addCase(deletePackage.pending, (state) => {
        state.packageActionLoading = true;
      })
      .addCase(deletePackage.fulfilled, (state) => {
        state.packageActionLoading = false;
        message.success("Package deleted");
      })
      .addCase(deletePackage.rejected, (state, action) => {
        state.packageActionLoading = false;
        message.error(action.payload || "Failed to delete package");
      })
      .addCase(restorePackage.pending, (state) => {
        state.packageActionLoading = true;
      })
      .addCase(restorePackage.fulfilled, (state) => {
        state.packageActionLoading = false;
        message.success("Package restored");
      })
      .addCase(restorePackage.rejected, (state, action) => {
        state.packageActionLoading = false;
        message.error(action.payload || "Failed to restore package");
      })
      .addCase(uploadPackageImages.pending, (state) => {
        state.packageActionLoading = true;
      })
      .addCase(uploadPackageImages.fulfilled, (state) => {
        state.packageActionLoading = false;
      })
      .addCase(uploadPackageImages.rejected, (state, action) => {
        state.packageActionLoading = false;
        message.error(action.payload || "Package saved, but the images failed to upload");
      })

      // ── customizations: catalog ──
      .addCase(fetchCustomizationOptions.pending, (state) => {
        state.customizationsLoading = true;
      })
      .addCase(fetchCustomizationOptions.fulfilled, (state, action) => {
        state.customizationsLoading = false;
        state.customizationOptions = action.payload;
      })
      .addCase(fetchCustomizationOptions.rejected, (state, action) => {
        state.customizationsLoading = false;
        message.error(action.payload || "Failed to load customizations");
      })

      // ── customizations: catalog create/update/delete ──
      .addCase(createCustomizationOption.pending, (state) => {
        state.customizationActionLoading = true;
      })
      .addCase(createCustomizationOption.fulfilled, (state, action) => {
        state.customizationActionLoading = false;
        state.customizationOptions.push(action.payload);
        message.success("Customization option created");
      })
      .addCase(createCustomizationOption.rejected, (state, action) => {
        state.customizationActionLoading = false;
        message.error(action.payload || "Failed to create customization option");
      })
      .addCase(updateCustomizationOption.pending, (state) => {
        state.customizationActionLoading = true;
      })
      .addCase(updateCustomizationOption.fulfilled, (state, action) => {
        state.customizationActionLoading = false;
        const idx = state.customizationOptions.findIndex((o) => o.id === action.payload.id);
        if (idx !== -1) state.customizationOptions[idx] = action.payload;
        message.success("Customization option updated");
      })
      .addCase(updateCustomizationOption.rejected, (state, action) => {
        state.customizationActionLoading = false;
        message.error(action.payload || "Failed to update customization option");
      })
      .addCase(deleteCustomizationOption.pending, (state) => {
        state.customizationActionLoading = true;
      })
      .addCase(deleteCustomizationOption.fulfilled, (state, action) => {
        state.customizationActionLoading = false;
        state.customizationOptions = state.customizationOptions.filter(
          (o) => o.id !== action.payload,
        );
        message.success("Customization option deleted");
      })
      .addCase(deleteCustomizationOption.rejected, (state, action) => {
        state.customizationActionLoading = false;
        message.error(action.payload || "Failed to delete customization option");
      })

      // ── customizations: per-package links ──
      .addCase(fetchPackageCustomizations.pending, (state) => {
        state.customizationsLoading = true;
      })
      .addCase(fetchPackageCustomizations.fulfilled, (state, action) => {
        state.customizationsLoading = false;
        state.packageCustomizations[action.payload.packageId] =
          action.payload.data;
      })
      .addCase(fetchPackageCustomizations.rejected, (state, action) => {
        state.customizationsLoading = false;
        message.error(
          action.payload || "Failed to load package customizations",
        );
      })
      .addCase(addPackageCustomization.pending, (state) => {
        state.customizationActionLoading = true;
      })
      .addCase(addPackageCustomization.fulfilled, (state) => {
        state.customizationActionLoading = false;
        message.success("Customization attached");
      })
      .addCase(addPackageCustomization.rejected, (state, action) => {
        state.customizationActionLoading = false;
        message.error(action.payload || "Failed to attach customization");
      })
      .addCase(removePackageCustomization.pending, (state) => {
        state.customizationActionLoading = true;
      })
      .addCase(removePackageCustomization.fulfilled, (state) => {
        state.customizationActionLoading = false;
        message.success("Customization detached");
      })
      .addCase(removePackageCustomization.rejected, (state, action) => {
        state.customizationActionLoading = false;
        message.error(action.payload || "Failed to detach customization");
      })

      // ── saved data (read-only) ──
      .addCase(fetchAllSavedData.pending, (state) => {
        state.savedDataLoading = true;
      })
      .addCase(fetchAllSavedData.fulfilled, (state, action) => {
        state.savedDataLoading = false;
        state.savedData = action.payload;
      })
      .addCase(fetchAllSavedData.rejected, (state, action) => {
        state.savedDataLoading = false;
        message.error(action.payload || "Failed to load saved data");
      })

      // ── bookings (read-only) ──
      .addCase(fetchAllPackageBookings.pending, (state) => {
        state.bookingsLoading = true;
      })
      .addCase(fetchAllPackageBookings.fulfilled, (state, action) => {
        state.bookingsLoading = false;
        state.bookings = action.payload;
      })
      .addCase(fetchAllPackageBookings.rejected, (state, action) => {
        state.bookingsLoading = false;
        message.error(action.payload || "Failed to load bookings");
      })
      .addCase(fetchPackageBookingById.pending, (state) => {
        state.bookingsLoading = true;
      })
      .addCase(fetchPackageBookingById.fulfilled, (state, action) => {
        state.bookingsLoading = false;
        state.selectedBooking = action.payload;
      })
      .addCase(fetchPackageBookingById.rejected, (state, action) => {
        state.bookingsLoading = false;
        message.error(action.payload || "Failed to load booking");
      })

      // ── custom package requests ──
      .addCase(fetchAllCustomPackageRequests.pending, (state) => {
        state.customPackageRequestsLoading = true;
      })
      .addCase(fetchAllCustomPackageRequests.fulfilled, (state, action) => {
        state.customPackageRequestsLoading = false;
        state.customPackageRequests = action.payload;
      })
      .addCase(fetchAllCustomPackageRequests.rejected, (state, action) => {
        state.customPackageRequestsLoading = false;
        message.error(action.payload || "Failed to load custom package requests");
      })
      .addCase(fetchCustomPackageRequestById.pending, (state) => {
        state.customPackageRequestsLoading = true;
      })
      .addCase(fetchCustomPackageRequestById.fulfilled, (state, action) => {
        state.customPackageRequestsLoading = false;
        state.selectedCustomPackageRequest = action.payload;
      })
      .addCase(fetchCustomPackageRequestById.rejected, (state, action) => {
        state.customPackageRequestsLoading = false;
        message.error(action.payload || "Failed to load custom package request");
      })
      .addCase(updateCustomPackageRequestStatus.pending, (state) => {
        state.customPackageRequestActionLoading = true;
      })
      .addCase(updateCustomPackageRequestStatus.fulfilled, (state, action) => {
        state.customPackageRequestActionLoading = false;
        const idx = state.customPackageRequests.data.findIndex(
          (r) => r.id === action.payload.id,
        );
        if (idx !== -1) state.customPackageRequests.data[idx] = action.payload;
        if (state.selectedCustomPackageRequest?.id === action.payload.id) {
          state.selectedCustomPackageRequest = action.payload;
        }
        message.success("Status updated");
      })
      .addCase(updateCustomPackageRequestStatus.rejected, (state, action) => {
        state.customPackageRequestActionLoading = false;
        message.error(action.payload || "Failed to update status");
      })
      .addCase(deleteCustomPackageRequest.pending, (state) => {
        state.customPackageRequestActionLoading = true;
      })
      .addCase(deleteCustomPackageRequest.fulfilled, (state, action) => {
        state.customPackageRequestActionLoading = false;
        state.customPackageRequests.data = state.customPackageRequests.data.filter(
          (r) => r.id !== action.payload,
        );
        message.success("Request deleted");
      })
      .addCase(deleteCustomPackageRequest.rejected, (state, action) => {
        state.customPackageRequestActionLoading = false;
        message.error(action.payload || "Failed to delete request");
      });
  },
});

export const {
  clearSelectedQuickTrip,
  clearSelectedPackage,
  clearSelectedBooking,
  clearSelectedCustomPackageRequest,
} = packagesSlice.actions;
export default packagesSlice.reducer;
